import { useCallback } from "react";

import { Form } from "antd";

import type { FormProps } from "antd";

/**
 * Extracts nested object keys for form field error clearing.
 * Antd forms allow for nested objects to be passed in. So we
 * need to be able to get the keys for the nested objects so that
 * we can clear the errors for the correct fields.
 *
 * @todo we should update the forms to use the FormList and remove this function
 * @param obj - The changed values object from the form
 * @returns An array of keys that have changed
 *
 */
export const getNestedKeysObj = (obj: { [key: string]: any }): string[] => {
    // Initialize an empty array to hold the keys
    let keys: string[] = [];

    // Get the first key in the object
    const firstKey = Object.keys(obj)[0];

    if (firstKey) {
        // Push the first key to the array
        keys.push(firstKey);

        const firstValue = obj[firstKey];

        // If the value of the first key is an object, recursively get its keys
        if (typeof firstValue === "object" && firstValue !== null) {
            // If the value is an array, consider the first element of the array
            if (Array.isArray(firstValue) && firstValue.length > 0) {
                keys = keys.concat(getNestedKeysObj(firstValue[0]));
            } else {
                keys = keys.concat(getNestedKeysObj(firstValue));
            }
        }
    }

    return keys;
};

/**
 * Extracts nested object keys for FormList-based form field error clearing.
 * Antd forms allow for nested objects to be passed in. So we
 * need to be able to get the keys for the nested objects so that
 * we can clear the errors for the correct fields.
 * This version is specifically for when using the AntD FormList component.
 *
 * @param obj - The changed values object from the form
 * @returns An array of keys (strings and numbers) that have changed
 *
 */
export const getNestedKeysList = (obj: { [key: string]: any }): Array<number | string> => {
    let keys: Array<number | string> = [];

    const firstKey = Object.keys(obj)[0];

    if (firstKey) {
        keys.push(firstKey);

        const firstValue = obj[firstKey];

        if (typeof firstValue === "object" && firstValue !== null) {
            if (Array.isArray(firstValue)) {
                for (let i = 0; i < firstValue.length; i++) {
                    if (firstValue[i] !== null && firstValue[i] !== undefined) {
                        keys.push(i);
                        keys = keys.concat(getNestedKeysList(firstValue[i]));
                        break;
                    }
                }
            } else {
                keys = keys.concat(getNestedKeysList(firstValue));
            }
        }
    }

    return keys;
};

/**
 * Props for the RVForm component.
 * Extends Ant Design's FormProps with additional functionality.
 */
type RVFormProps = {
    /** CSS class name for the form */
    className?: string;
    /** React children to render inside the form */
    children: React.ReactNode;
    /**
     * Whether the form uses FormList for repeatable fields.
     * This affects how nested field errors are cleared.
     * If you have a form with repeatable fields, try to use the FormList component
     * and pass this as true.
     */
    usesFormList?: boolean;
} & FormProps;

/**
 * A wrapper around the Ant Design Form component that provides enhanced UX features.
 *
 * Key features:
 * - Automatically clears field errors when values change (provides better UX during typing)
 * - Supports both regular nested objects and FormList-based repeatable fields
 * - Validates only on form submission by default
 * - Maintains all standard Ant Design Form functionality
 *
 * @param props - RVForm component props
 * @param props.className - CSS class name for the form
 * @param props.children - React children to render inside the form
 * @param props.usesFormList - Whether the form uses FormList for repeatable fields
 * @param props.form - Ant Design form instance
 * @param props.onValuesChange - Callback fired when form values change
 * @returns JSX.Element - The rendered form component
 */
export const RVForm = ({ className, children, usesFormList, ...rest }: RVFormProps) => {
    const { form, onValuesChange } = rest;
    const clearErrorsOnChange = useCallback(
        (changedValues: any) => {
            if (changedValues) {
                const getKeys = usesFormList ? getNestedKeysList : getNestedKeysObj;
                const name = getKeys(changedValues);
                if (name) {
                    form?.setFields([
                        {
                            name,
                            errors: [],
                        },
                    ]);
                }
            }
        },
        [form, usesFormList]
    );

    const handleValuesChange = useCallback(
        (changedValues: any, allValues: any) => {
            onValuesChange?.(changedValues, allValues);
            clearErrorsOnChange(changedValues);
        },
        [clearErrorsOnChange, onValuesChange]
    );

    return (
        <Form {...rest} validateTrigger={"onSubmit"} className={className} onValuesChange={handleValuesChange}>
            {children}
        </Form>
    );
};
